/**
 * @file routes/_app/me/notifications.tsx
 * @description 通知偏好設定頁面路由（需認證）。
 *
 * 功能：
 * - 通知管道設定（站內 / Email / 瀏覽器推播）
 * - 通知類型設定（任務分配 / 到期提醒 / 審批通知）
 * - PATCH /api/me/notification-prefs 儲存偏好（mock 本地儲存）
 *
 * 視覺：PageShell + SectionCard + FormSection + 自定義設定行
 */

import { createFileRoute } from '@tanstack/react-router';
import { Button, Switch } from 'antd';
import { message } from '@/lib/antd-app';
import { Bell, Mail, Monitor, Smartphone } from 'lucide-react';
import { PageShell, SectionCard, FormSection } from '@/components/shell';
import { color, leading, radius, space, text, weight } from '@/styles/tokens';

/** 路由定義 */
export const Route = createFileRoute('/_app/me/notifications')({
  component: NotificationsSettingsPage,
});

/** 通知管道設定項 */
const NOTIFICATION_CHANNELS = [
  {
    key: 'inApp',
    title: '站內通知',
    description: '系統內即時訊息，出現在右上角通知鈴',
    icon: <Bell size={14} />,
    defaultChecked: true,
  },
  {
    key: 'email',
    title: '電子郵件通知',
    description: '透過 Email 發送重要事項提醒',
    icon: <Mail size={14} />,
    defaultChecked: true,
  },
  {
    key: 'push',
    title: '瀏覽器推播',
    description: '桌面瀏覽器推播通知（需授予權限）',
    icon: <Monitor size={14} />,
    defaultChecked: false,
  },
  {
    key: 'sms',
    title: '簡訊通知',
    description: '僅限緊急事項（額外費用）',
    icon: <Smartphone size={14} />,
    defaultChecked: false,
  },
];

/** 通知類型設定項 */
const NOTIFICATION_TYPES = [
  {
    key: 'taskAssign',
    title: '任務分配通知',
    description: '當有新任務指派給您時通知',
    defaultChecked: true,
  },
  {
    key: 'expireRemind',
    title: '到期提醒',
    description: '各階段作業期限前提醒（7日前/3日前/當日）',
    defaultChecked: true,
  },
  {
    key: 'reviewResult',
    title: '審批通知',
    description: '您的提交被批准或退回時通知',
    defaultChecked: true,
  },
  {
    key: 'decisionVote',
    title: '驗證決定投票',
    description: '委員會投票開始或有新投票時通知',
    defaultChecked: true,
  },
  {
    key: 'certExpiry',
    title: '證書到期提醒',
    description: '您負責的客戶證書即將到期時通知',
    defaultChecked: true,
  },
  {
    key: 'systemAlert',
    title: '系統公告',
    description: '系統維護通知、重要公告',
    defaultChecked: false,
  },
];

/**
 * 通知設定行（開關 + 標題 + 描述）。
 */
function NotificationRow({
  title,
  description,
  icon,
  defaultChecked,
  isLast = false,
}: {
  title: string;
  description: string;
  icon?: React.ReactNode;
  defaultChecked: boolean;
  isLast?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${space[4]}px 0`,
        borderBottom: isLast ? 'none' : `1px solid ${color.border.subtle}`,
        gap: space[4],
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: space[3], minWidth: 0 }}>
        {icon && (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: radius.md,
              background: color.brand.primarySoft,
              color: color.brand.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: text.md,
              fontWeight: weight.semibold,
              color: color.text.primary,
              lineHeight: leading.snug,
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: text.sm, color: color.text.tertiary, marginTop: 2 }}>
            {description}
          </div>
        </div>
      </div>
      <Switch defaultChecked={defaultChecked} />
    </div>
  );
}

/**
 * 通知偏好設定頁面。
 */
function NotificationsSettingsPage() {
  return (
    <PageShell title="通知偏好" description="管理您希望接收通知的方式與類型">
      <div style={{ maxWidth: 720 }}>
        <SectionCard>
          {/* 通知管道 */}
          <FormSection title="通知管道" description="選擇接收通知的方式" bordered>
            <div>
              {NOTIFICATION_CHANNELS.map((ch, idx) => (
                <NotificationRow
                  key={ch.key}
                  title={ch.title}
                  description={ch.description}
                  icon={ch.icon}
                  defaultChecked={ch.defaultChecked}
                  isLast={idx === NOTIFICATION_CHANNELS.length - 1}
                />
              ))}
            </div>
          </FormSection>

          {/* 通知類型 */}
          <FormSection title="通知類型" description="選擇要接收的通知種類">
            <div>
              {NOTIFICATION_TYPES.map((t, idx) => (
                <NotificationRow
                  key={t.key}
                  title={t.title}
                  description={t.description}
                  defaultChecked={t.defaultChecked}
                  isLast={idx === NOTIFICATION_TYPES.length - 1}
                />
              ))}
            </div>
            <div style={{ marginTop: space[5] }}>
              <Button type="primary" onClick={() => void message.success('通知偏好已儲存')}>
                儲存設定
              </Button>
            </div>
          </FormSection>
        </SectionCard>
      </div>
    </PageShell>
  );
}
